import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createMemoStyles, defaultBaseline, defaultFonts, visuallyHidden, getState, uniqueId, clsx, safeRest } from '@wds/utils';
import { useTheme } from 'react-jss';
import { IconCheck } from '@wds/icons';
import { Typography } from '@wds/typography';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
      type = theme.type,
      spacing = theme.spacing,
      motion = theme.motion,
      radius = theme.radius;
  var checkbox = {
    bgColorOn: palette.primary.main,
    iconColorOn: palette.primary.contrast
  };
  var bounceFrames = {
    '@keyframes bounce': {
      '0%': {
        opacity: 0,
        transform: 'scale(0.6)',
        animationTimingFunction: 'cubic-bezier(0, 0, .1, 1)'
      },
      '10%': {
        opacity: 1
      },
      '33%': {
        opacity: 1,
        transform: 'scale(1.4)',
        animationTimingFunction: 'cubic-bezier(.2, 0, 0, 1)'
      },
      '100%': {
        opacity: 1,
        transform: 'scale(1)'
      }
    }
  };
  var scaleDownFrames = {
    '@keyframes scaleDown': {
      from: {
        opacity: 1,
        transform: 'scale(1)',
        animationTimingFunction: 'cubic-bezier(1, 0, 1, 1)'
      },
      '75%': {
        opacity: 1
      },
      '100%': {
        opacity: 0,
        transform: 'scale(0.4)'
      }
    }
  };
  return _objectSpread(_objectSpread(_objectSpread({}, bounceFrames), scaleDownFrames), {}, {
    wrenchCheckbox: function wrenchCheckbox() {
      return _objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), defaultFonts(theme)), {}, {
        position: 'relative',
        display: 'inline-flex',
        minHeight: spacing[5],
        minWidth: spacing[5],
        lineHeight: "".concat(spacing[5], "px")
      });
    },
    wrenchCheckboxFaux: function wrenchCheckboxFaux() {
      return _objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), defaultFonts(theme)), {}, {
        content: '""',
        display: 'inline-block',
        height: spacing[5],
        width: spacing[5],
        flexGrow: 0,
        flexShrink: 0,
        fontSize: type.fontSize.body,
        border: "1px solid ".concat(palette.border.main),
        background: palette.background.light,
        borderRadius: radius.small,
        transition: "all 50ms ".concat(motion.curve.exit, " 100ms"),
        verticalAlign: 'top',
        textAlign: 'left',
        '&:empty::after': {
          content: '3',
          fontFamily: type.fontFamily.icon,
          color: checkbox.iconColorOn,
          lineHeight: 1
        },
        '> svg[class^="wrenchIcons"], &:empty::after': {
          position: 'absolute',
          top: spacing[1],
          left: spacing[1],
          opacity: 0,
          textAlign: 'center',
          fill: checkbox.iconColorOn,
          animation: '$scaleDown 150ms forwards'
        }
      });
    },
    wrenchCheckboxLabel: function wrenchCheckboxLabel() {
      return {
        padding: [0, 0, 0, spacing[2]],
        whiteSpace: 'normal'
      };
    },
    wrenchCheckboxIndeterminate: function wrenchCheckboxIndeterminate() {
      return {
        '& + $wrenchCheckboxFaux': {
          borderColor: checkbox.bgColorOn,
          background: checkbox.bgColorOn,
          transform: 'scale(1)',
          transition: "transform 200ms ".concat(motion.curve.bounceEnd)
        },
        '& + $wrenchCheckboxFaux > svg[class^="wrenchIcons"]': visuallyHidden,
        '& + $wrenchCheckboxFaux:after': {
          position: 'absolute',
          top: '10px',
          left: '5px',
          display: 'block',
          content: '""',
          background: palette.background.light,
          width: spacing[3],
          height: '2px'
        }
      };
    },
    wrenchCheckboxNative: function wrenchCheckboxNative(_ref) {
      var isIndeterminate = _ref.isIndeterminate;
      return _objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), defaultFonts(theme)), {}, {
        position: 'absolute',
        cursor: 'inherit',
        opacity: 0,
        width: spacing[5],
        height: spacing[5],
        zIndex: 1,
        left: 0,
        '&:focus:checked + $wrenchCheckboxFaux': {
          borderColor: '#fff !important',
          boxShadow: "0 0 0 1px ".concat(palette.primary.main, " !important")
        },
        '&:focus + $wrenchCheckboxFaux, &:hover + $wrenchCheckboxFaux': {
          borderColor: palette.border.hover,
          transform: 'scale(1.1)',
          transition: "transform 200ms ".concat(motion.curve.ease, ", border-color 75ms linear 50ms")
        },
        '&:active + $wrenchCheckboxFaux': {
          transform: 'scale(0.7)',
          transition: "transform 100ms ".concat(motion.curve.bounceStart)
        },
        '&:checked + $wrenchCheckboxFaux': {
          borderColor: checkbox.bgColorOn,
          background: checkbox.bgColorOn,
          transform: 'scale(1)',
          transition: "transform 200ms ".concat(motion.curve.bounceEnd)
        },
        '&:checked + $wrenchCheckboxFaux > svg[class^="wrenchIcons"]': {
          animation: "$bounce 200ms forwards"
        },
        '&:checked + $wrenchCheckboxFaux &:empty::after': {
          animation: "$bounce 200ms forwards"
        },
        '&:disabled + $wrenchCheckboxFaux, &:disabled ~ $wrenchCheckboxLabel': {
          opacity: '0.5',
          transform: 'scale(1)'
        }
      }, isIndeterminate && {
        '& + $wrenchCheckboxFaux': {
          borderColor: checkbox.bgColorOn,
          background: checkbox.bgColorOn,
          transform: 'scale(1)',
          transition: "transform 200ms ".concat(motion.curve.bounceEnd)
        },
        '& + $wrenchCheckboxFaux > svg[class^="wrenchIcons"]': visuallyHidden,
        '& + $wrenchCheckboxFaux:after': {
          position: 'absolute',
          top: '10px',
          left: '5px',
          display: 'block',
          content: '""',
          background: palette.background.light,
          width: spacing[3],
          height: '2px'
        }
      });
    }
  });
});
var _excluded = ["ariaLabel", "checked", "defaultChecked", "disabled", "id", "indeterminate", "innerRef", "label", "name", "onBlur", "onChange"];

var Checkbox = function Checkbox(_ref) {
  var ariaLabel = _ref.ariaLabel,
      checked = _ref.checked,
      defaultChecked = _ref.defaultChecked,
      disabled = _ref.disabled,
      id = _ref.id,
      indeterminate = _ref.indeterminate,
      innerRef = _ref.innerRef,
      label = _ref.label,
      name = _ref.name,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(!!defaultChecked),
      _useState2 = _slicedToArray(_useState, 2),
      isChecked = _useState2[0],
      setChecked = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocused = _useState4[0],
      setFocused = _useState4[1];

  var _useState5 = useState(!!indeterminate),
      _useState6 = _slicedToArray(_useState5, 2),
      isIndeterminate = _useState6[0],
      setIndeterminate = _useState6[1];

  useEffect(function () {
    setIndeterminate(indeterminate);
  }, [indeterminate]);
  var checkedState = getState({
    isChecked: isChecked
  }, typeof checked !== 'undefined' && {
    isChecked: checked
  }).isChecked;
  var inputID = id || uniqueId('checkbox');
  var theme = useTheme();

  var _useStyles = useStyles({
    theme: theme
  }),
      wrenchCheckboxNative = _useStyles.wrenchCheckboxNative,
      wrenchCheckboxIndeterminate = _useStyles.wrenchCheckboxIndeterminate,
      wrenchCheckbox = _useStyles.wrenchCheckbox,
      wrenchCheckboxLabel = _useStyles.wrenchCheckboxLabel,
      wrenchCheckboxFaux = _useStyles.wrenchCheckboxFaux;

  var nativeClasses = clsx(wrenchCheckboxNative, isIndeterminate && wrenchCheckboxIndeterminate || {});

  var handleBlur = function handleBlur(e) {
    setFocused(!isFocused);
    if (onBlur) onBlur(e);
  };

  var handleFocus = function handleFocus() {
    setFocused(!isFocused);
  };

  var handleChange = function handleChange(e) {
    setChecked(!checkedState);
    e.persist();
    if (onChange) onChange(e);
  };

  return /*#__PURE__*/React.createElement("span", {
    className: wrenchCheckbox
  }, /*#__PURE__*/React.createElement("input", Object.assign({
    "aria-label": ariaLabel,
    className: nativeClasses,
    "aria-checked": checkedState,
    checked: checkedState,
    disabled: disabled,
    id: inputID,
    name: name,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onChange: handleChange,
    tabIndex: disabled ? -1 : 0,
    type: "checkbox",
    ref: innerRef
  }, safeRest(rest))), /*#__PURE__*/React.createElement("span", {
    className: wrenchCheckboxFaux,
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(IconCheck, {
    color: "light",
    "aria-hidden": "true"
  })), label ? /*#__PURE__*/React.createElement("span", {
    className: wrenchCheckboxLabel
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: inputID
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body",
    component: "span"
  }, label))) : null);
};

Checkbox.displayName = "Checkbox";
Checkbox.propTypes = {
  ariaLabel: PropTypes.string,

  /**
   * Selected checkbox state.
   */
  checked: PropTypes.bool,

  /**
   * Use for uncontrolled Checkboxes.
   */
  defaultChecked: PropTypes.bool,

  /**
   * Disabled flag
   */
  disabled: PropTypes.bool,

  /**
   * Identifier
   */
  // eslint-disable-next-line consistent-return
  id: function id(props, propName) {
    if (typeof props[propName] !== 'string' && typeof props.label !== 'string' && typeof props.ariaLabel !== 'string') {
      return new Error('Accessibility Error: If `label` or `ariaLabel` is not provided the `id` prop is required.');
    }
  },

  /**
   * Indeterminate checkbox style.
   */
  indeterminate: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),

  /**
   * Text label to the right side of the checkbox.
   */
  label: PropTypes.node,

  /**
   * Checkbox element name attribute.
   */
  name: PropTypes.string,

  /**
   * Send in a custom onClose function.
   */
  onBlur: PropTypes.func,

  /**
   * Send in a custom onClose function.
   */
  onChange: PropTypes.func
};
Checkbox.defaultProps = {
  ariaLabel: undefined,
  checked: undefined,
  defaultChecked: false,
  disabled: false,
  id: undefined,
  indeterminate: false,
  innerRef: undefined,
  label: undefined,
  name: undefined,
  onBlur: undefined,
  onChange: undefined
};
Checkbox.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Checkbox",
  "props": {
    "ariaLabel": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "checked": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Selected checkbox state."
    },
    "defaultChecked": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Use for uncontrolled Checkboxes."
    },
    "disabled": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Disabled flag"
    },
    "id": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "custom",
        "raw": "(props, propName) => {\n  if (\n    typeof props[propName] !== 'string' &&\n    typeof props.label !== 'string' &&\n    typeof props.ariaLabel !== 'string'\n  ) {\n    return new Error(\n      'Accessibility Error: If `label` or `ariaLabel` is not provided the `id` prop is required.'\n    );\n  }\n}"
      },
      "required": false,
      "description": "Identifier"
    },
    "indeterminate": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Indeterminate checkbox style."
    },
    "innerRef": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "union",
        "value": [{
          "name": "func"
        }, {
          "name": "object"
        }, {
          "name": "string"
        }]
      },
      "required": false,
      "description": ""
    },
    "label": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Text label to the right side of the checkbox."
    },
    "name": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Checkbox element name attribute."
    },
    "onBlur": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Send in a custom onClose function."
    },
    "onChange": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Send in a custom onClose function."
    }
  }
};
export { Checkbox };